import type Client from './websocket';
import { API_BASE_CHANGED_EVENT, SESSION_ENDING_EVENT, readBearer, readBearerOrigin, tryShellRefresh } from '../auth/fetch';

interface AuthenticationState {
  recover(rejectedBearer: string, timeoutMs: number): Promise<boolean>;
  dispose(): void;
}

const bindings = new WeakMap<Client, AuthenticationState>();

/** One shell binding for every authenticated mux channel, regardless of protocol. */
export function attachSocketAuthentication(socket: Client, onRefreshRejected?: () => void): () => void {
  bindings.get(socket)?.dispose();
  let bearer = readBearer();
  let disposed = false;
  let pending: Promise<boolean> | null = null;
  const failedScopes = new Set<string>();
  socket.setBearer(bearer);
  socket.setBearerProvider(readBearer);
  // The hello frame only goes to the origin that issued the bearer.
  socket.setBearerOriginProvider(readBearerOrigin);
  // A new API base closes the socket to the old one before anything redials.
  const onApiBaseChanged = () => {
    if (!disposed) socket.retarget();
  };

  // The shell replaces the session (and reloads): close the mux at once and
  // forget the bearer, so nothing runs as the old account in between.
  const onSessionEnding = () => {
    if (disposed) return;
    bearer = '';
    socket.setBearer('');
    socket.stop();
    // The binding ends with the session: a pending recovery must not reconnect
    // as the next account and replay what the old one was rejected on.
    state.dispose();
  };

  const syncCredentials = () => {
    const current = readBearer();
    if (current === bearer || disposed) return;
    bearer = current;
    if (!pending) failedScopes.clear();
    socket.setBearer(current);
    if (!current) { socket.stop(); return; }
    // Reopen all upstream handshakes, not just the outer mux identity.
    if (!socket.reauth()) socket.reconnectNow();
  };

  const state: AuthenticationState = {
    recover(rejectedBearer, timeoutMs) {
      if (disposed) return Promise.resolve(false);
      if (pending) return pending;
      // Defer work so synchronous auth-changed callbacks see the shared promise.
      pending = Promise.resolve().then(async () => {
        if (readBearer() === rejectedBearer && !await tryShellRefresh()) {
          if (!disposed) onRefreshRejected?.();
          return false;
        }
        if (disposed || !readBearer()) return false;
        syncCredentials();
        if (!socket.isReady()) socket.reconnectNow();
        const ready = await socket.waitUntilReady(timeoutMs);
        return ready && !disposed;
      }).finally(() => { pending = null; });
      return pending;
    },
    dispose() {
      disposed = true;
      window.removeEventListener('formica:auth-changed', syncCredentials);
      window.removeEventListener(API_BASE_CHANGED_EVENT, onApiBaseChanged);
      window.removeEventListener(SESSION_ENDING_EVENT, onSessionEnding);
      for (const off of unsubs) off();
      if (bindings.get(socket) === state) bindings.delete(socket);
    },
  };

  const unsubs = [
    socket.on('authentication-failed', ({ scope, bearer: rejectedBearer }: { scope: string; bearer: string }) => {
      if (scope === '_mux') socket.stop();
      // A refreshed bearer still refused by the SAME endpoint must not start
      // an endless refresh loop. Successful admission re-arms only that scope.
      if (failedScopes.has(scope)) return;
      failedScopes.add(scope);
      void state.recover(rejectedBearer, 30_000);
    }),
    socket.on('ready', () => failedScopes.delete('_mux')),
    socket.on('channel-open', (scope: string) => failedScopes.delete(scope)),
  ];
  window.addEventListener('formica:auth-changed', syncCredentials);
  window.addEventListener(API_BASE_CHANGED_EVENT, onApiBaseChanged);
  window.addEventListener(SESSION_ENDING_EVENT, onSessionEnding);
  bindings.set(socket, state);
  return () => state.dispose();
}

/** Protocol adapters call this only after an explicit authentication rejection.
 * They own whether a rejected operation is safe to repeat; this never replays it. */
export function recoverSocketAuthentication(socket: Client, rejectedBearer: string, timeoutMs = 30_000): Promise<boolean> {
  return bindings.get(socket)?.recover(rejectedBearer, timeoutMs) ?? Promise.resolve(false);
}
